import { ExternalSchoolFields } from 'main/gql/externalSchool'
import { SchoolListingType } from 'main/gql/globalTypes'
import { externalSchoolPagePath, schoolListingPagePath, schoolPagePath } from 'main/routes'
import { momentFromDateAndTime } from 'main/util/dateTime'
import { profileEnrollmentPricing } from 'main/util/profile'
import { UnreachableCaseError } from 'main/util/ts'
import { findLatestTour } from './common/lead'
import { CalloutSchoolListingFields } from './gql'
import {
  ILeadStatus,
  ITargetData,
  LeadStatusType,
  School,
  Target,
  TargetType,
  UpcomingEvent,
  UpcomingEventType,
} from './types'

function getTourMoment(leadStatus: ILeadStatus) {
  if (leadStatus.type !== LeadStatusType.Scheduled) return null

  const {
    lead: { school, tours },
  } = leadStatus
  const latestTour = findLatestTour(tours)

  return latestTour ? momentFromDateAndTime(latestTour.beginsAt, school.timezone) : null
}

// HACK (alex): Upcoming events may come from lead data (e.g. scheduled tours), or from school data
// (e.g. upcoming gatherings). When `leadStatus` is passed in (like it would be on the my programs
// page), we only want to show upcoming events from lead data (e.g. to avoid showing both upcoming
// tours and upcoming gatherings on the my programs page). Otherwise, we want to use the passed in
// `upcomingEvent` as the displayed event.
export function getDisplayedUpcomingEvent(
  leadStatus: ILeadStatus | null,
  upcomingEvent: UpcomingEvent | null,
): UpcomingEvent | null {
  if (!leadStatus) return upcomingEvent

  const tourMoment = getTourMoment(leadStatus)
  if (!tourMoment) return null

  return {
    __type: UpcomingEventType.Tour,
    beginsAt: tourMoment,
  }
}

export function getBookmarkButtonSchoolIdForTarget(target: Target) {
  switch (target._type) {
    case TargetType.ExternalSchool:
      return null
    case TargetType.School:
      return target.school.id
    case TargetType.SchoolListing:
      return target.listing.schoolId
    default:
      throw new UnreachableCaseError(target)
  }
}

export function getDataForTarget(target: Target) {
  switch (target._type) {
    case TargetType.ExternalSchool:
      return getDataForExternalSchool(target.externalSchool)
    case TargetType.School:
      return getDataForSchool(target.school)
    case TargetType.SchoolListing:
      return getDataForSchoolListing(target.listing)
    default:
      throw new UnreachableCaseError(target)
  }
}

export function getDataForExternalSchool(externalSchool: ExternalSchoolFields): ITargetData {
  const { headerImageUrl, name, profile } = externalSchool
  const hours =
    profile && profile.hoursStart && profile.hoursEnd
      ? { start: profile.hoursStart, end: profile.hoursEnd }
      : null
  const pricing =
    profile && profile.enrollment ? profileEnrollmentPricing(profile.enrollment) : null

  return {
    hours,
    imageUrl: headerImageUrl,
    isVerified: false,
    name,
    pagePath: externalSchoolPagePath(externalSchool),
    philosophy: profile ? profile.philosophy : null,
    pricing,
    programType: null,
    rating: null,
    networkRating: profile ? profile.networkRating : null,
    whitelabelRating: profile ? profile.whitelabelRating : null,
  }
}

export function getDataForSchool(school: School): ITargetData {
  const { profile, programType, aggregateRatings } = school
  const hours =
    profile && profile.hoursStart && profile.hoursEnd
      ? { start: profile.hoursStart, end: profile.hoursEnd }
      : null
  const imageUrl =
    (profile && profile.primaryProfileMedia[0] && profile.primaryProfileMedia[0].media.url) || null
  const pricing =
    profile && profile.enrollment ? profileEnrollmentPricing(profile.enrollment) : null
  const rating =
    aggregateRatings && aggregateRatings.overall
      ? { count: aggregateRatings.count, overall: aggregateRatings.overall }
      : null

  return {
    hours,
    imageUrl,
    isVerified: true,
    name: school.name,
    pagePath: schoolPagePath(school),
    philosophy: profile ? profile.philosophy : null,
    pricing,
    programType,
    rating,
    networkRating: profile ? profile.networkRating : null,
    whitelabelRating: profile ? profile.whitelabelRating : null,
  }
}

export function getDataForSchoolListing(listing: CalloutSchoolListingFields): ITargetData {
  const { aggregateRatings, imageUrl, profile, programType } = listing
  const hours =
    profile && profile.hoursStart && profile.hoursEnd
      ? { start: profile.hoursStart, end: profile.hoursEnd }
      : null
  const pricing =
    profile && profile.enrollment ? profileEnrollmentPricing(profile.enrollment) : null
  const rating =
    aggregateRatings && aggregateRatings.overall
      ? { count: aggregateRatings.count, overall: aggregateRatings.overall }
      : null

  return {
    hours,
    imageUrl,
    isVerified: listing.type === SchoolListingType.INTERNAL,
    name: listing.name,
    pagePath: schoolListingPagePath(listing),
    philosophy: profile ? profile.philosophy : null,
    pricing,
    programType,
    rating,
    networkRating: profile ? profile.networkRating : null,
    whitelabelRating: profile ? profile.whitelabelRating : null,
  }
}
